import { jsx } from 'react/jsx-runtime';
import { createContext, useRef, useState, useMemo, useCallback, useEffect, useContext } from 'react';
import '@dynamic-labs/multi-wallet';
import '@dynamic-labs/sdk-api';
import '../../../config/ApiEndpoint.js';
import '../../../shared/logger.js';
import '@dynamic-labs/iconic';
import '../../../context/ViewContext/ViewContext.js';
import '../../../utils/constants/colors.js';
import '../../../shared/consts/index.js';
import { useInternalDynamicContext } from '../../../context/DynamicContext/useInternalDynamicContext.js';
import '../../../context/ErrorContext/ErrorContext.js';
import 'react-dom';
import '../../../components/ShadowDOM/ShadowDOM.js';
import '../../../context/ThemeContext/ThemeContext.js';
import 'react-focus-lock';
import '../../../components/Transition/ZoomTransition/ZoomTransition.js';
import '../../../components/Transition/SlideInUpTransition/SlideInUpTransition.js';
import '../../../components/Transition/OpacityTransition/OpacityTransition.js';
import '../../../components/IconButton/IconButton.js';
import 'ethers';
import { useKYCFlag } from '../../../utils/hooks/useKYCFlag/useKYCFlag.js';
import { useMultiWalletFlag } from '../../../utils/hooks/useMultiWalletFlag/useMultiWalletFlag.js';
import '../../../context/MockContext/MockContext.js';
import '../../../context/CaptchaContext/CaptchaContext.js';
import '../../../utils/hooks/useUserUpdateRequest/useUserUpdateRequest.js';

const DynamicWidgetContext = createContext(undefined);
const DynamicWidgetContextProvider = ({ children, }) => {
    const widgetRef = useRef(null);
    const inlineControlsRef = useRef(null);
    const availableWalletsContainerRef = useRef(null);
    const { setShowDynamicUserProfile, showDynamicUserProfile } = useInternalDynamicContext();
    const [newWalletId, setNewWalletId] = useState('');
    const [walletIdToUnlink, setWalletIdToUnlink] = useState(undefined);
    const isKYCEnabled = useKYCFlag();
    const isMultiWalletEnabled = useMultiWalletFlag();
    const initialView = useMemo(() => (isKYCEnabled && !isMultiWalletEnabled ? 'profile' : 'index'), [isKYCEnabled, isMultiWalletEnabled]);
    const [dynamicWidgetView, setDynamicWidgetView] = useState(initialView);
    const goToProfileView = useCallback(() => {
        if (isKYCEnabled)
            return setDynamicWidgetView('profile');
        setDynamicWidgetView('index');
    }, [isKYCEnabled, isMultiWalletEnabled]);
    // set default view on close
    useEffect(() => {
        if (!showDynamicUserProfile) {
            // wait for exit animation to finish
            setTimeout(() => {
                setDynamicWidgetView(initialView);
            }, 200);
        }
    }, [initialView, showDynamicUserProfile]);
    // set correct initial view once isKYCEnabled and isMultiWalletEnabled values are correctly set
    // *isKYCEnabled is always false when the context initializes, so the initial view is set to index
    useEffect(() => {
        setDynamicWidgetView(initialView);
    }, [initialView, isKYCEnabled, isMultiWalletEnabled]);
    const value = useMemo(() => ({
        availableWalletsContainerRef,
        dynamicWidgetView,
        goToProfileView,
        inlineControlsRef,
        isOpen: showDynamicUserProfile,
        newWalletId,
        setDynamicWidgetView,
        setIsOpen: setShowDynamicUserProfile,
        setNewWalletId,
        setWalletIdToUnlink,
        walletIdToUnlink,
        widgetRef,
    }), [
        dynamicWidgetView,
        goToProfileView,
        newWalletId,
        setShowDynamicUserProfile,
        showDynamicUserProfile,
        walletIdToUnlink,
    ]);
    return (jsx(DynamicWidgetContext.Provider, Object.assign({ value: value }, { children: children })));
};
const useWidgetContext = () => {
    const context = useContext(DynamicWidgetContext);
    if (context === undefined) {
        throw new Error('usage of useWidgetContext not wrapped in `DynamicWidgetContextProvider`.');
    }
    return context;
};

export { DynamicWidgetContext, DynamicWidgetContextProvider, useWidgetContext };
